import { Grid, Button, CircularProgress, Alert } from '@mui/material';
import React, { useEffect, useRef, useCallback, useImperativeHandle, useState } from 'react';
import { MediaType, PlayerEventType, type PlayerStatusChangedEvent } from '@accurate-player/accurate-player-core';
import { Segment, SegmentPlayer, type SegmentService as PlayerAPI } from '@accurate-player/accurate-player-segment';
import { HotkeyPlugin, SmoothTimeUpdatePlugin, SmoothTimeUpdateEventType } from '@accurate-player/accurate-player-plugins';
import styled from '@emotion/styled';
import { CSSTransition } from 'react-transition-group';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCurrentSegment, selectTimelineFramesCount, selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import {
  selectIsPlayerOpened,
  selectIsPlaying,
  selectPackagingTimelineFrameRate,
  selectPlayerCurrentFrame
} from '@/redux/selectors/packagingSelectors';
import { SegmentItem } from '@/redux/slices/packagingSlice.types';
import { setIsPlayerOpened, setIsPlaying, setIsToolboxDrawerOpen, setPlayerCurrentFrame } from '@/redux/slices/packagingSlice';
import PolarisIcon from '../../_common/PolarisIcon';
import { areAllAssignedSegmentsLoaded } from '@/components/Packaging/utils';
import OverlaysPreview from '@/components/Packaging/OverlaysPreview';
import PlayerControls from '@/components/Packaging/VideoPreview/PlayerControls';
import {
  NO_ASSIGNED_VIDEO_WARNING_ZINDEX,
  PLAYER_CONTROLS_CLASSNAME,
  PLAYER_LOADER_ZINDEX,
  PLAYER_TOP_PANEL_CLASSNAME,
  PLAYER_TOP_PANEL_ZINDEX
} from '@/components/Packaging/constants';
import { selectConstants } from '@/redux/selectors/constantsSelectors';
import { useToast } from '@/components/_common/Toast';

const prepareSegments = (segments: SegmentItem[]): Segment[] =>
  segments.flatMap((segment) => {
    if (segment.isIntermediate) {
      return [];
    }

    const { source, start, end } = segment;

    const playerSegment: Segment = {
      mediaType: MediaType.Video,
      src: typeof source?.previewUrl === 'string' ? source.previewUrl : undefined,
      start,
      end,
      sourceStart: source?.start ?? 0,
      sourceEnd: source?.end ?? end - start
    };

    return [playerSegment];
  });

const transitionDuration = 300;

interface Props {
  playerApiRef: React.MutableRefObject<PlayerAPI | null>;
  isLoadingPreviewURLs: boolean;
  retryLoadingFailedPreview(): void;
}

const VideoPreview: React.FC<Props> = ({ playerApiRef, isLoadingPreviewURLs, retryLoadingFailedPreview }) => {
  const dispatch = useAppDispatch();
  const {
    packaging: { accurate_key }
  } = useAppSelector(selectConstants);
  const { error } = useToast();
  const isPlayerOpened = useAppSelector(selectIsPlayerOpened);
  const isPlaying = useAppSelector(selectIsPlaying);
  const segments = useAppSelector(selectTimelineSegments);
  const currentSegment = useAppSelector(selectCurrentSegment);
  const frameRate = useAppSelector(selectPackagingTimelineFrameRate);
  const currentFrame = useAppSelector(selectPlayerCurrentFrame);
  const timelineFramesCount = useAppSelector(selectTimelineFramesCount);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [player, setPlayer] = useState<SegmentPlayer | null>(null);
  const hotkeyPluginRef = useRef<HotkeyPlugin | null>(null);
  const smoothTimeUpdatePluginRef = useRef<SmoothTimeUpdatePlugin | null>(null);
  const [isLoadingVideoStream, setIsLoadingVideoStream] = useState(false);
  const [videoHeight, setVideoHeight] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartHeight, setDragStartHeight] = useState(300);

  const isCurrentSegmentAssigned = currentSegment && Boolean(currentSegment.source);
  const currentSegmentHasPreviewUrl = currentSegment && typeof currentSegment.source?.previewUrl === 'string';
  const previewLoadingError = currentSegment?.source?.previewLoadingError?.reason;

  useImperativeHandle<PlayerAPI | null, PlayerAPI | null>(playerApiRef, () => player?.api ?? null, [player]);

  const destroyPlayer = useCallback(() => {
    if (player) {
      player.destroy().catch(console.error);
      setPlayer(null);
    }
    hotkeyPluginRef.current?.destroy();
    smoothTimeUpdatePluginRef.current?.destroy();
  }, [player]);

  // init SegmentPlayer
  useEffect(
    () => {
      if (!isPlayerOpened || !videoRef.current || !areAllAssignedSegmentsLoaded(segments)) {
        return;
      }

      /**
       * We want to skip the first TimeUpdate event, because after segments are changed we re-initialize player,
       * which causes TimeUpdate event to be emitted with 0 value, which causes PlayHead to jump to the 0 frame, and
       * we want to avoid it.
       */
      let skipTimeUpdate = true;

      if (!accurate_key) {
        error('Can not find accurate_key');
        return;
      }

      // Initialize the player
      const segmentPlayer = new SegmentPlayer(videoRef.current, accurate_key, {
        aspectRatioSettings: {
          aspectRatio: '16:9',
          stretchToFit: true,
          background: '#333'
        }
      });

      hotkeyPluginRef.current = new HotkeyPlugin(segmentPlayer);
      smoothTimeUpdatePluginRef.current = new SmoothTimeUpdatePlugin(segmentPlayer);

      const endFrameExclusive = true;

      const preparedSegments = prepareSegments(segments);
      const isFrameOutsideTimeline = currentFrame > timelineFramesCount; // this can happen if `currentFrame` is on the last segment and user removes that last segment.
      const targetFrame = isFrameOutsideTimeline ? 0 : currentFrame;

      segmentPlayer.api.loadSegments(
        preparedSegments,
        {
          numerator: frameRate[0],
          denominator: frameRate[1]
        },
        endFrameExclusive,
        targetFrame
      );

      dispatch(setPlayerCurrentFrame(targetFrame));

      segmentPlayer.on(SmoothTimeUpdateEventType.TimeUpdate, async (event) => {
        if (skipTimeUpdate) {
          skipTimeUpdate = false;
          return;
        }

        dispatch(setPlayerCurrentFrame(event.currentFrame));
      });

      setPlayer(segmentPlayer);
    },
    // This use effect relies on `segments` but we don't specify `segments` in dependencies for a reason.
    //  The whole `VideoPreview` component is unmounted and mounted again in the parent component when `segments` change.
    //  If we add `segments` to dependencies array, then `SegmentPlayer` will be initialized twice every time `segments` change.
    [isPlayerOpened]
  );

  useEffect(() => {
    if (!player) return;

    const handleStatusChange = (event: PlayerStatusChangedEvent) => {
      const { status } = event;
      const isPlayingNew = !status.paused;
      const isLoadingNew = !status.loaded;
      if (isPlaying !== isPlayingNew) {
        dispatch(setIsPlaying(isPlayingNew));
      }
      if (isLoadingVideoStream !== isLoadingNew) {
        setIsLoadingVideoStream(isLoadingNew);
      }
    };

    player.on(PlayerEventType.StatusChanged, handleStatusChange);

    return () => {
      if (!player) return;
      player.off(PlayerEventType.StatusChanged, handleStatusChange);
    };
  }, [player, isPlaying, isLoadingVideoStream]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      const deltaY = e.clientY - dragStartY;
      const newHeight = Math.max(200, Math.min(800, dragStartHeight + deltaY));
      setVideoHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (isDragging) {
        document.body.style.cursor = '';
      }
    };
  }, [isDragging, dragStartY, dragStartHeight]);

  const handleButtonMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartHeight(videoHeight);
  };

  useEffect(() => () => destroyPlayer(), []);

  const togglePreviewButtonClickHandler = () => {
    const newIsPlayerOpened = !isPlayerOpened;
    dispatch(setIsPlayerOpened(newIsPlayerOpened));
    if (newIsPlayerOpened) {
      dispatch(setIsToolboxDrawerOpen(false));
    }
  };

  const handleVideoContainerClick: React.MouseEventHandler<HTMLDivElement> = async (e) => {
    if (!player) return;

    if (e.target instanceof Element && e.target.matches('[data-prevent-video-container-click], [data-prevent-video-container-click] *')) {
      /**
       * Do not toggle video play if the element that the user clicked is inside the element with the `data-prevent-video-container-click`attribute.
       */
      return;
    }

    if (player) {
      await player.api.togglePlay();
    }
  };

  return (
    <VideoPreviewWrapper>
      <CSSTransition in={isPlayerOpened} timeout={transitionDuration} classNames="video" unmountOnExit>
        <StyledGrid container alignItems="center" justifyContent="center" height={videoHeight}>
          <VideoContainer
            ref={videoContainerRef}
            className={isPlaying ? undefined : 'paused'}
            onClick={handleVideoContainerClick}
            height={videoHeight}
          >
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- caption tracks are not available for user-uploaded media previews */}
            <video ref={videoRef} crossOrigin="anonymous" />
            {(isLoadingVideoStream || isLoadingPreviewURLs) && <Loader size={'5rem'} />}
            <TopPanel className={PLAYER_TOP_PANEL_CLASSNAME}>{currentSegment && currentSegment.label}</TopPanel>
            <OverlaysPreview />
            {player && videoContainerRef.current && <PlayerControls player={player} videoContainer={videoContainerRef.current} />}
            {!isCurrentSegmentAssigned && (
              <NoAssignedVideoWarning>
                <Alert severity="warning">No asset assigned to this segment</Alert>
              </NoAssignedVideoWarning>
            )}
            {!isLoadingPreviewURLs && isCurrentSegmentAssigned && !currentSegmentHasPreviewUrl && (
              <NoAssignedVideoWarning>
                <Alert severity="warning">{previewLoadingError ?? 'Current segment does not have valid preview URL'}</Alert>
                <Button sx={{ mt: 1 }} color="warning" variant="outlined" data-prevent-video-container-click onClick={retryLoadingFailedPreview}>
                  Retry
                </Button>
              </NoAssignedVideoWarning>
            )}
          </VideoContainer>
        </StyledGrid>
      </CSSTransition>
      <ExpansionLine onMouseDown={handleButtonMouseDown} open={+isPlayerOpened} />
      <ButtonContainer>
        <StyledButton
          color="secondary"
          variant="contained"
          size="small"
          onClick={togglePreviewButtonClickHandler}
          endIcon={<PolarisIcon icon={isPlayerOpened ? 'up' : 'down'} />}
          sx={{}}
          open={+isPlayerOpened}
        >
          {!isPlayerOpened ? 'Watch Preview' : 'Hide Preview'}
        </StyledButton>
      </ButtonContainer>
    </VideoPreviewWrapper>
  );
};

export default VideoPreview;

const VideoPreviewWrapper = styled.div`
  position: relative;
`;

const StyledGrid = styled(Grid)<{ height: number }>`
  &.video-enter {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
  }

  &.video-enter-active {
    max-height: ${({ height }) => height + 100}px;
    opacity: 1;
    transition:
      max-height ${transitionDuration}ms ease,
      opacity ${transitionDuration}ms ease;
  }

  &.video-exit {
    max-height: ${({ height }) => height + 100}px;
    opacity: 1;
    overflow: hidden;
  }

  &.video-exit-active {
    max-height: 0;
    opacity: 0;
    transition:
      max-height ${transitionDuration}ms ease,
      opacity ${transitionDuration}ms ease;
  }
`;

const NoAssignedVideoWarning = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${NO_ASSIGNED_VIDEO_WARNING_ZINDEX};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ExpansionLine = styled.div<{ open?: number }>`
  height: 2px;
  background-color: ${({ theme }) => theme.palette.secondary.main};
  width: 100%;
  cursor: ${({ open }) => (open ? 'ns-resize' : 'default')};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const VideoContainer = styled.div<{ height: number }>`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  width: calc((${({ height }) => height}px * 16) / 9);
  height: ${({ height }) => height}px;
  overflow: hidden;

  &:not(.paused) .${PLAYER_CONTROLS_CLASSNAME}, &:not(.paused) .${PLAYER_TOP_PANEL_CLASSNAME} {
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover .${PLAYER_CONTROLS_CLASSNAME}, &:hover .${PLAYER_TOP_PANEL_CLASSNAME} {
    opacity: 1;
  }
`;

const Loader = styled(CircularProgress)`
  position: absolute;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  z-index: ${PLAYER_LOADER_ZINDEX};
`;

const TopPanel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${PLAYER_TOP_PANEL_ZINDEX};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(2, 2, 3)};
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 100%);
  color: #ffffff;
`;

const StyledButton = styled(Button)<{ open: number }>`
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  cursor: pointer;
`;
